import React from 'react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Config-driven control types (registry + ChartControls + useChartControls)
// ---------------------------------------------------------------------------

export type ScheduleDateBasisValue = 'baseline' | 'current';

/** Spread basis for planned-units histogram / S-curve (server calendars vs calendar-day MVP). */
export type UnitsSpreadBasisValue = 'working' | 'calendar';

export type ControlDefinition =
  | { type: 'show-count'; options: (number | 'all')[]; default: number | 'all' }
  | { type: 'sort'; default: 'worst-first' | 'best-first' }
  | { type: 'include-negatives'; default: boolean }
  | { type: 'show-labels'; default: boolean }
  | { type: 'show-baseline'; default: boolean }
  | { type: 'schedule-date-basis'; default: ScheduleDateBasisValue }
  | { type: 'include-milestones'; default: boolean }
  | { type: 'units-spread-basis'; default: UnitsSpreadBasisValue }
  | { type: 'search'; placeholder?: string };

export type ControlValues = {
  'show-count': number | 'all';
  'sort': 'worst-first' | 'best-first';
  'include-negatives': boolean;
  'show-labels': boolean;
  'show-baseline': boolean;
  'schedule-date-basis': ScheduleDateBasisValue;
  'include-milestones': boolean;
  'units-spread-basis': UnitsSpreadBasisValue;
  'search': string;
};

// ---------------------------------------------------------------------------
// Config-driven API
// ---------------------------------------------------------------------------

/** Optional hints for disabling controls when backend data is unavailable */
export interface ChartControlsMeta {
  plannedUnitsEngineEnabled?: boolean;
}

export interface ChartControlsProps {
  controls: ControlDefinition[];
  values: Partial<ControlValues>;
  onChange: (controlType: ControlDefinition['type'], value: unknown) => void;
  className?: string;
  meta?: ChartControlsMeta;
}

const buttonActiveClass = 'bg-primary text-primary-foreground';
const buttonInactiveClass = 'bg-card text-foreground hover:bg-muted/50';

function ShowCountControl({
  definition,
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'show-count' }>;
  value: ControlValues['show-count'] | undefined;
  onChange: (v: ControlValues['show-count']) => void;
}) {
  const current = value ?? definition.default;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground">Show</span>
      <div className="flex items-center border border-border rounded-md overflow-hidden">
        {definition.options.map((opt) => (
          <button
            key={String(opt)}
            type="button"
            onClick={() => onChange(opt)}
            className={cn(
              'px-2 py-1 transition-colors text-xs',
              current === opt ? buttonActiveClass : buttonInactiveClass
            )}
          >
            {opt === 'all' ? 'All' : String(opt)}
          </button>
        ))}
      </div>
    </div>
  );
}

function SortControl({
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'sort' }>;
  value: ControlValues['sort'] | undefined;
  onChange: (v: ControlValues['sort']) => void;
}) {
  const current = value ?? 'worst-first';
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground">Sort</span>
      <div className="flex items-center border border-border rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => onChange('worst-first')}
          className={cn(
            'px-2 py-1 transition-colors text-xs',
            current === 'worst-first' ? buttonActiveClass : buttonInactiveClass
          )}
        >
          Worst first
        </button>
        <button
          type="button"
          onClick={() => onChange('best-first')}
          className={cn(
            'px-2 py-1 transition-colors text-xs',
            current === 'best-first' ? buttonActiveClass : buttonInactiveClass
          )}
        >
          Best first
        </button>
      </div>
    </div>
  );
}

function IncludeNegativesControl({
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'include-negatives' }>;
  value: ControlValues['include-negatives'] | undefined;
  onChange: (v: ControlValues['include-negatives']) => void;
}) {
  const checked = value ?? true;
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-border"
      />
      <span className="text-muted-foreground">Include negative scores</span>
    </label>
  );
}

function ShowLabelsControl({
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'show-labels' }>;
  value: ControlValues['show-labels'] | undefined;
  onChange: (v: ControlValues['show-labels']) => void;
}) {
  const checked = value ?? true;
  return (
    <label className="flex items-center gap-2 whitespace-nowrap cursor-pointer select-none text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-border"
      />
      <span>Show labels on chart</span>
    </label>
  );
}

function ShowBaselineControl({
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'show-baseline' }>;
  value: ControlValues['show-baseline'] | undefined;
  onChange: (v: ControlValues['show-baseline']) => void;
}) {
  const checked = value ?? false;
  return (
    <label className="flex items-center gap-2 whitespace-nowrap cursor-pointer select-none text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-border"
      />
      <span>Show baseline</span>
    </label>
  );
}

function ScheduleDateBasisControl({
  definition,
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'schedule-date-basis' }>;
  value: ControlValues['schedule-date-basis'] | undefined;
  onChange: (v: ControlValues['schedule-date-basis']) => void;
}) {
  const current = value ?? definition.default;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground">Dates</span>
      <div className="flex items-center border border-border rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => onChange('baseline')}
          className={cn(
            'px-2 py-1 transition-colors text-xs',
            current === 'baseline' ? buttonActiveClass : buttonInactiveClass
          )}
        >
          Target dates
        </button>
        <button
          type="button"
          onClick={() => onChange('current')}
          className={cn(
            'px-2 py-1 transition-colors text-xs',
            current === 'current' ? buttonActiveClass : buttonInactiveClass
          )}
        >
          Early/current
        </button>
      </div>
    </div>
  );
}

function UnitsSpreadBasisControl({
  definition,
  value,
  onChange,
  meta,
}: {
  definition: Extract<ControlDefinition, { type: 'units-spread-basis' }>;
  value: ControlValues['units-spread-basis'] | undefined;
  onChange: (v: ControlValues['units-spread-basis']) => void;
  meta?: ChartControlsMeta;
}) {
  const engineOn = Boolean(meta?.plannedUnitsEngineEnabled);
  const effective: ControlValues['units-spread-basis'] =
    !engineOn && (value ?? definition.default) === 'working' ? 'calendar' : (value ?? definition.default);
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground">Units spread</span>
      <div className="flex items-center border border-border rounded-md overflow-hidden">
        <button
          type="button"
          disabled={!engineOn}
          title={
            engineOn ? 'Spread using P6 activity calendars (clndr_data)' : 'Requires schedule analytics calendar engine'
          }
          onClick={() => engineOn && onChange('working')}
          className={cn(
            'px-2 py-1 transition-colors text-xs disabled:opacity-40 disabled:cursor-not-allowed',
            effective === 'working' ? buttonActiveClass : buttonInactiveClass
          )}
        >
          Working days
        </button>
        <button
          type="button"
          onClick={() => onChange('calendar')}
          className={cn(
            'px-2 py-1 transition-colors text-xs',
            effective === 'calendar' ? buttonActiveClass : buttonInactiveClass
          )}
        >
          Calendar days
        </button>
      </div>
    </div>
  );
}

function IncludeMilestonesControl({
  definition,
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'include-milestones' }>;
  value: ControlValues['include-milestones'] | undefined;
  onChange: (v: ControlValues['include-milestones']) => void;
}) {
  const checked = value ?? definition.default;
  return (
    <label className="flex items-center gap-2 whitespace-nowrap cursor-pointer select-none text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-border"
      />
      <span>Include milestone units</span>
    </label>
  );
}

function SearchControl({
  definition,
  value,
  onChange,
}: {
  definition: Extract<ControlDefinition, { type: 'search' }>;
  value: ControlValues['search'] | undefined;
  onChange: (v: ControlValues['search']) => void;
}) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={definition.placeholder ?? 'Search…'}
      className="w-40 px-2 py-1 text-xs border border-border rounded-md bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
    />
  );
}

type ControlComponentProps = {
  definition: ControlDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  meta?: ChartControlsMeta;
};

const CONTROL_COMPONENTS: Record<ControlDefinition['type'], React.FC<ControlComponentProps>> = {
  'show-count': (props) => (
    <ShowCountControl
      definition={props.definition as Extract<ControlDefinition, { type: 'show-count' }>}
      value={props.value as ControlValues['show-count'] | undefined}
      onChange={props.onChange as (v: ControlValues['show-count']) => void}
    />
  ),
  'sort': (props) => (
    <SortControl
      definition={props.definition as Extract<ControlDefinition, { type: 'sort' }>}
      value={props.value as ControlValues['sort'] | undefined}
      onChange={props.onChange as (v: ControlValues['sort']) => void}
    />
  ),
  'include-negatives': (props) => (
    <IncludeNegativesControl
      definition={props.definition as Extract<ControlDefinition, { type: 'include-negatives' }>}
      value={props.value as ControlValues['include-negatives'] | undefined}
      onChange={props.onChange as (v: ControlValues['include-negatives']) => void}
    />
  ),
  'show-labels': (props) => (
    <ShowLabelsControl
      definition={props.definition as Extract<ControlDefinition, { type: 'show-labels' }>}
      value={props.value as ControlValues['show-labels'] | undefined}
      onChange={props.onChange as (v: ControlValues['show-labels']) => void}
    />
  ),
  'show-baseline': (props) => (
    <ShowBaselineControl
      definition={props.definition as Extract<ControlDefinition, { type: 'show-baseline' }>}
      value={props.value as ControlValues['show-baseline'] | undefined}
      onChange={props.onChange as (v: ControlValues['show-baseline']) => void}
    />
  ),
  'schedule-date-basis': (props) => (
    <ScheduleDateBasisControl
      definition={props.definition as Extract<ControlDefinition, { type: 'schedule-date-basis' }>}
      value={props.value as ControlValues['schedule-date-basis'] | undefined}
      onChange={props.onChange as (v: ControlValues['schedule-date-basis']) => void}
    />
  ),
  'include-milestones': (props) => (
    <IncludeMilestonesControl
      definition={props.definition as Extract<ControlDefinition, { type: 'include-milestones' }>}
      value={props.value as ControlValues['include-milestones'] | undefined}
      onChange={props.onChange as (v: ControlValues['include-milestones']) => void}
    />
  ),
  'units-spread-basis': (props) => (
    <UnitsSpreadBasisControl
      definition={props.definition as Extract<ControlDefinition, { type: 'units-spread-basis' }>}
      value={props.value as ControlValues['units-spread-basis'] | undefined}
      onChange={props.onChange as (v: ControlValues['units-spread-basis']) => void}
      meta={props.meta}
    />
  ),
  'search': (props) => (
    <SearchControl
      definition={props.definition as Extract<ControlDefinition, { type: 'search' }>}
      value={props.value as ControlValues['search'] | undefined}
      onChange={props.onChange as (v: ControlValues['search']) => void}
    />
  ),
};

/**
 * Config-driven controls strip for focus mode. Renders only the controls
 * declared in the chart's registry config (show-count, sort, show-labels, search, etc.).
 */
export const ChartControls: React.FC<ChartControlsProps> = ({
  controls,
  values,
  onChange,
  className,
  meta,
}) => {
  if (!controls.length) return null;
  const hasSearch = controls.some((c) => c.type === 'search');
  const nonSearchControls = hasSearch ? controls.filter((c) => c.type !== 'search') : controls;
  const searchControl = hasSearch ? controls.find((c) => c.type === 'search') : null;
  return (
    <div className={cn('flex flex-wrap items-center gap-3 text-xs', className)}>
      {nonSearchControls.map((control, idx) => {
        const Component = CONTROL_COMPONENTS[control.type];
        if (!Component) return null;
        return (
          <Component
            key={`${control.type}-${idx}`}
            definition={control}
            value={values[control.type]}
            onChange={(v) => onChange(control.type, v)}
            meta={meta}
          />
        );
      })}
      <div className="flex-1" />
      {searchControl && (() => {
        const Component = CONTROL_COMPONENTS[searchControl.type];
        return Component ? (
          <Component
            key={searchControl.type}
            definition={searchControl}
            value={values[searchControl.type]}
            onChange={(v) => onChange(searchControl.type, v)}
            meta={meta}
          />
        ) : null;
      })()}
    </div>
  );
};
